// Pure helpers for the Analytics → Videos tab (CFS-96). Kept free of React/remote so they can
// be unit-tested directly.

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Read ?from&to (yyyy-MM-dd) out of a location.search string; invalid or missing → nulls. */
export function parseRangeFromSearch(search) {
	const params = new URLSearchParams(search || "");
	const from = params.get("from");
	const to = params.get("to");
	return {
		from: from && DATE_RE.test(from) ? from : null,
		to: to && DATE_RE.test(to) ? to : null,
	};
}

/** The tab's default window — mirrors the backend's server-side default of the last 30 days. */
export function defaultRange(today = new Date()) {
	const to = formatDateParam(today);
	const fromDate = new Date(today);
	fromDate.setDate(fromDate.getDate() - 30);
	return { from: formatDateParam(fromDate), to };
}

export function formatDateParam(date) {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	const d = String(date.getDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
}

/** Rewrite from/to on an existing search string, preserving any other params. Nulls remove them. */
export function buildRangeSearch(search, from, to) {
	const params = new URLSearchParams(search || "");
	if (from) params.set("from", from); else params.delete("from");
	if (to) params.set("to", to); else params.delete("to");
	const str = params.toString();
	return str ? `?${str}` : "";
}

/**
 * The product filter chosen in the AnalyticsSourceSelector, as repeated product params
 * ("product=3&product=7"). Both "nothing selected" and "everything selected" mean "All" →
 * empty string. That distinction matters for videos where fund-based pages don't care:
 * unpublished videos belong to no product, so any product params at all would drop them —
 * only a strict subset should narrow the list.
 */
export function selectedProductParams(bannerAdditionalInfo) {
	if (!bannerAdditionalInfo?.platforms) return "";
	const products = bannerAdditionalInfo.platforms.flatMap((platform) => platform.products || []);
	const selected = products.filter((product) => product.selected);
	if (selected.length === 0 || selected.length === products.length) return "";
	return selected.map((product) => `product=${product.id}`).join("&");
}

/** Assemble the list/detail endpoint query string from range + product parts. "" when neither. */
export function buildAnalyticsQuery(range, productParams) {
	const parts = [];
	if (range?.from && range?.to) parts.push(`from=${range.from}&to=${range.to}`);
	if (productParams) parts.push(productParams);
	return parts.length ? `?${parts.join("&")}` : "";
}

/** "96.4 hrs" / "1 hr" / "42m" / "18s" — for the Total Watch Time KPI. */
export function secondsToHoursLabel(seconds) {
	if (seconds == null) return "—";
	if (seconds >= 3600) {
		const hours = seconds / 3600;
		const label = hours >= 100 ? Math.round(hours) : Math.round(hours * 10) / 10;
		return `${label} ${label === 1 ? "hr" : "hrs"}`;
	}
	if (seconds >= 60) return `${Math.floor(seconds / 60)}m`;
	return `${Math.round(seconds)}s`;
}

/** "3:42" — minutes:seconds, for watch-time cells and the video duration meta line. */
export function secondsToMmSs(seconds) {
	if (seconds == null) return "—";
	const total = Math.round(seconds); // round first so 119.6 → "2:00", never "1:60"
	const mins = Math.floor(total / 60);
	const secs = total % 60;
	return `${mins}:${String(secs).padStart(2, "0")}`;
}

/** "67%" (whole numbers), or an em dash when the backend couldn't compute it. */
export function formatPercent(percent) {
	if (percent == null) return "—";
	return `${Math.round(percent)}%`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const hasText = (value) => Boolean(value && String(value).trim());

/** A "general" video (CFS-108): registered server-side with a category such as "What's Topical". */
export function isGeneralVideo(row) {
	return hasText(row?.generalCategory);
}

/**
 * The table's Portfolio/Manager column: fund name, else ticker, else (general videos — What's
 * Topical) the configured title, else (About-the-Manager videos — attached to a manager group, never
 * published) the manager name, else (unpublished — no metadata at all) the raw Bunny title so the row
 * is still identifiable by its filename. Order mirrors the server's attribution precedence
 * (published > general > manager); the server never emits both general and manager fields on one row.
 */
export function fundLabel(row) {
	if (hasText(row?.fundName)) return row.fundName;
	if (hasText(row?.ticker)) return row.ticker;
	if (isGeneralVideo(row) && hasText(row?.generalTitle)) return row.generalTitle;
	if (hasText(row?.managerName)) return row.managerName;
	return row?.bunnyTitle || "—";
}

/**
 * The table's Type column: the publishing template's name, else the general category (e.g. "What's
 * Topical"), else "About Manager" for videos attached to a manager group, else null (unpublished —
 * the list renders "—").
 */
export function typeLabel(row) {
	if (hasText(row?.templateName)) return row.templateName;
	if (isGeneralVideo(row)) return row.generalCategory;
	if (hasText(row?.managerName)) return "About Manager";
	return null;
}

/**
 * List visibility: published documents, About-Manager videos and general videos belong in the All
 * Videos table. Unpublished library videos with no attribution (test uploads, detached files) are
 * hidden. Published is judged by any PublishedVideoRef-sourced field so a row with a blank template
 * or fund name still shows.
 */
export function isListedVideo(row) {
	if (hasText(row?.managerName)) return true;
	if (isGeneralVideo(row)) return true;
	return Boolean(
		(row?.templateName && row.templateName.trim())
		|| row?.publishedDate
		|| (row?.ticker && String(row.ticker).trim())
		|| (row?.fundName && row.fundName.trim())
	);
}

/**
 * The Published column's per-row sort key (yyyy-MM-dd, lexicographic = chronological):
 * the published date, else the Bunny upload date for About-Manager and general videos. Null
 * otherwise so unpublished rows keep sorting last.
 */
export function publishedSortKey(row) {
	if (row?.publishedDate) return row.publishedDate;
	if ((hasText(row?.managerName) || isGeneralVideo(row)) && row.dateUploaded) {
		return String(row.dateUploaded).slice(0, 10);
	}
	return null;
}

/** "2026-07-15" → "15 Jul 2026" (matches the backend displayName's d MMM yyyy); null when unparseable. */
export function isoDateLabel(iso) {
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
	if (!match) return null;
	return `${Number(match[3])} ${MONTHS[Number(match[2]) - 1]} ${match[1]}`;
}

/** Published column label: "15 Jul 2026" from the row's published/upload date; "—" when neither. */
export function publishedDateLabel(row) {
	return isoDateLabel(publishedSortKey(row)) || "—";
}

/**
 * Tooltip for Plays numbers: where the count comes from and the date it's valid from.
 * Plays are Bunny's counters, so within a date-ranged payload they're valid from the range
 * start; when the library is over the fan-out cap the figures are all-time instead.
 */
export function playsTooltip(dateRanged, from) {
	const source = "Plays are counted by Bunny wherever the video is played.";
	if (dateRanged === false) return `${source} All-time figures — the library is too large for date filtering.`;
	const label = isoDateLabel(from);
	return label ? `${source} Data valid from ${label}.` : source;
}

/**
 * Tooltip for Unique Viewers numbers: first-party capture only exists from the first recorded
 * player event (the payload's `capturedFrom`), so earlier dates legitimately read 0.
 */
export function uniqueViewersTooltip(capturedFrom) {
	const source = "Unique viewers are counted from plays inside the portal.";
	const label = isoDateLabel(capturedFrom);
	return label
		? `${source} Data valid from ${label}, when in-portal capture began.`
		: `${source} No in-portal plays captured yet.`;
}

/**
 * Null-safe text sort for the fund/template/published columns. Blank and "—" values sort last
 * in both directions so unpublished rows never crowd the top of a sorted column.
 */
export function textComparator(get, ascending) {
	return (a, b) => {
		const av = String(get(a) || "").trim();
		const bv = String(get(b) || "").trim();
		const aBlank = !av || av === "—";
		const bBlank = !bv || bv === "—";
		if (aBlank || bBlank) return aBlank && bBlank ? 0 : aBlank ? 1 : -1;
		return ascending ? av.localeCompare(bv) : bv.localeCompare(av);
	};
}

/** Bunny's dateUploaded ("2026-07-01T02:12:33") → "1/7/2026"; null-safe. */
export function formatUploadedDate(dateUploaded) {
	if (!dateUploaded) return "—";
	const date = new Date(dateUploaded);
	if (isNaN(date.getTime())) return "—";
	return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
}

/** Rows → CSV-export shape for the All Videos table (raw numbers, not display strings). */
export function tableExportData(rows) {
	return (rows || []).map((row) => ({
		fund: fundLabel(row),
		bunnyTitle: row.bunnyTitle || "",
		plays: row.plays || 0,
		uniqueViewers: row.uniqueViewers || 0,
		avgWatchTimeSeconds: row.avgWatchTimeSeconds || 0,
		percentWatched: row.percentWatched == null ? "" : Math.round(row.percentWatched * 10) / 10,
		engagementScore: row.engagementScore == null ? "" : row.engagementScore,
		ticker: row.ticker || "",
		manager: row.managerName || "",
		template: typeLabel(row) || "",
		publishedDate: publishedSortKey(row) || "",
		videoGuid: row.guid,
	}));
}

/** Summary → CSV-export shape for the KPI row. */
export function summaryExportData(summary, dateRanged) {
	if (!summary) return [];
	return [
		{ metric: "Total Plays", value: summary.totalPlays },
		{ metric: "Unique Viewers", value: summary.uniqueViewers ?? 0 },
		{ metric: "Total Watch Time (seconds)", value: summary.totalWatchTimeSeconds },
		{ metric: "Avg % Watched", value: summary.avgPercentWatched == null ? "" : Math.round(summary.avgPercentWatched * 10) / 10 },
		{ metric: "Most Viewed", value: summary.mostViewedName || "" },
		{ metric: "Videos", value: summary.videoCount },
		{ metric: "Date Filtered", value: dateRanged ? "yes" : "no (all-time figures)" },
	];
}
